import { makeNewCommExternalId } from 'Containers/App/utils';
import { STANDARD_EQ_TO_QUESTION_CODE } from 'Containers/ProfilePage/constants';
import {
  ClaimsSummary,
  ClientSurveyResponses,
  genderValues,
  Member,
  MEMBER_TYPE,
  MemberSectionConfigFields,
  PlannedProcedures,
} from 'Containers/ProfilePage/types';
import { JVPEligibilityQuestion, RawClaimsSummary } from 'Utils/apiTypes';
import Logger from 'Utils/logger';
import { validDependantAge, validPolicyholderAge, validSpouseAge } from 'Utils/validators';

export const determineHiddenEqs = (
  clientSurvey: ClientSurveyResponses | null,
  integratedEqs: JVPEligibilityQuestion[],
): string[] => {
  // Hide EQs if they have a Question Code and if we have an answer for them
  const hiddenEligibilityQuestions: string[] = [];
  Object.entries(clientSurvey || {}).forEach(([questionId, answer]) => {
    const questionCodeOrId =
      questionId in STANDARD_EQ_TO_QUESTION_CODE ? STANDARD_EQ_TO_QUESTION_CODE[questionId] : questionId;

    if (
      answer !== null &&
      (integratedEqs.find((q) => q.jv_eligibility_question_id === questionCodeOrId) ||
        integratedEqs.find((q) => q.question_code === questionCodeOrId))
    ) {
      hiddenEligibilityQuestions.push(questionId);
    }
  });

  return hiddenEligibilityQuestions;
};

function makeInitialUtilization() {
  return {
    inpatient_days: 0,
    specialist_visits: 0,
    pcp_visits: 0,
  };
}

function makeInitialPlannedProcedures(): PlannedProcedures {
  return {
    inpatient_procedures: 0,
    outpatient_procedures: 0,
    routine_therapy_visits: 0,
    mental_health_visits: 0,
  };
}

export const newMemberState = (memberType: Member['member_type']): Member =>
  makeNewMemberState(memberType, makeNewCommExternalId());

export const newMemberStateWithExistingExternalId = (
  memberType: Member['member_type'],
  externalId: string,
): Member => makeNewMemberState(memberType, externalId);

const makeNewMemberState = (memberType: Member['member_type'], externalId: string): Member => ({
  external_id: externalId,
  member_type: memberType,
  isValid: false,
  pristine: true,
  isComplete: false,
  age: '',
  gender: '',
  uses_tobacco: null,
  on_medicare: null,
  uses_prescriptions: null,
  prescriptions: [],
  uses_utilization: null,
  utilization: makeInitialUtilization(),
  uses_planned_procedures: null,
  planned_procedures: makeInitialPlannedProcedures(),
  income: '',
  isSkipIncome: false,
  isCovered: true,
});

function shouldCollectTobacco(
  member_type: MEMBER_TYPE,
  collectTobaccoUsageForPolicyholder: boolean,
  collectTobaccoUsageForSpouse: boolean,
) {
  return (
    (member_type === MEMBER_TYPE.POLICYHOLDER && collectTobaccoUsageForPolicyholder) ||
    ([MEMBER_TYPE.SPOUSE, MEMBER_TYPE.SPOUSE_OR_DOMESTIC_PARTNER, MEMBER_TYPE.DOMESTIC_PARTNER].includes(
      member_type,
    ) &&
      collectTobaccoUsageForSpouse)
  );
}

export function isMemberComplete(
  member: Member,
  memberConfig: MemberSectionConfigFields | Record<string, never> = {},
): boolean {
  const {
    collectTobaccoUsageForPolicyholder,
    collectTobaccoUsageForSpouse,
    collectIsSpouseOrDomesticPartner,
    isProductBc,
    sex_at_birth_enabled,
  } = memberConfig;
  const {
    age,
    gender,
    member_type,
    planned_procedures,
    prescriptions,
    uses_planned_procedures,
    uses_prescriptions,
    uses_tobacco,
    uses_utilization,
    utilization,
  } = member;

  const genderIsComplete = gender !== '' || !sex_at_birth_enabled;
  const ageIsComplete = age !== '';
  const utilizationIsComplete = uses_utilization !== null && !!utilization;
  const prescriptionsIsComplete =
    uses_prescriptions !== null && (!uses_prescriptions || prescriptions.length > 0);

  const plannedProceduresIsComplete = uses_planned_procedures !== null && !!planned_procedures;

  const spouseTypeIsComplete = !collectIsSpouseOrDomesticPartner || !isProductBc || member_type !== null;

  const collectTobaccoUsage = shouldCollectTobacco(
    member_type,
    collectTobaccoUsageForPolicyholder,
    collectTobaccoUsageForSpouse,
  );

  const tobaccoIsComplete = !collectTobaccoUsage || uses_tobacco !== null;

  const isComplete =
    ageIsComplete &&
    genderIsComplete &&
    utilizationIsComplete &&
    plannedProceduresIsComplete &&
    prescriptionsIsComplete &&
    spouseTypeIsComplete &&
    tobaccoIsComplete;

  return isComplete;
}

export function validateMember(
  member: Member,
  memberConfig: MemberSectionConfigFields | Record<string, never> = {},
): boolean {
  const {
    collectTobaccoUsageForPolicyholder,
    collectTobaccoUsageForSpouse,
    collectIsSpouseOrDomesticPartner,
    isProductBc,
    sex_at_birth_enabled,
  } = memberConfig;

  const { member_type, gender, age } = member;

  let isValidGender = genderValues.includes(gender) && gender !== '';
  if (!sex_at_birth_enabled) {
    // If sex at birth question is disabled, then allow empty genders.
    isValidGender = true;
  }

  let isValidAge = age !== '';

  if (isValidAge) {
    if (member_type === MEMBER_TYPE.POLICYHOLDER) {
      isValidAge = validPolicyholderAge(age);
    }
    if (
      member_type === MEMBER_TYPE.SPOUSE ||
      member_type === MEMBER_TYPE.SPOUSE_OR_DOMESTIC_PARTNER ||
      member_type === MEMBER_TYPE.DOMESTIC_PARTNER
    ) {
      isValidAge = validSpouseAge(age);
    } else if (member_type === MEMBER_TYPE.CHILD) {
      isValidAge = validDependantAge(age);
    }
  }

  const isChild = member_type === MEMBER_TYPE.CHILD;

  const collectTobaccoUsage = shouldCollectTobacco(
    member_type,
    collectTobaccoUsageForPolicyholder,
    collectTobaccoUsageForSpouse,
  );

  const isValidTobacco =
    !collectTobaccoUsage || isChild || parseInt(age, 10) < 18 || member.uses_tobacco !== null;

  const isValidSpouseOrDomesticPartner =
    !collectIsSpouseOrDomesticPartner ||
    !isProductBc ||
    member_type !== MEMBER_TYPE.SPOUSE_OR_DOMESTIC_PARTNER;

  const isValidPrescriptions =
    (member.uses_prescriptions && member.prescriptions.length > 0) || member.uses_prescriptions === false;

  const isValidUtilization =
    (member.uses_utilization && Object.values(member.utilization).some((i) => i > 0)) ||
    member.uses_utilization === false;

  const isValidPlannedProcedures =
    (member.uses_planned_procedures && Object.values(member.planned_procedures).some((i) => i > 0)) ||
    member.uses_planned_procedures === false ||
    member.uses_planned_procedures === null;

  // Only used for displaying error style on form input
  const isValid =
    isValidGender &&
    isValidAge &&
    isValidTobacco &&
    isValidSpouseOrDomesticPartner &&
    isValidPrescriptions &&
    isValidUtilization &&
    isValidPlannedProcedures;

  return isValid;
}

// Claims Summary API response validation Utils
const requiredStrings = [
  'claims_summary_id',
  'enrollment_event_id',
  'payer_name',
  'payer_code',
  'reported_at',
  'status',
];
const requiredNumbersOrNulls = ['deductible_cents', 'oop_toward_deductible_cents'];
const requiredNumbers = ['max_cents', 'oop_toward_max_cents'];

type ErrorMaker = (message: string) => Error;
function validateNumber(claimsSummary: any, property: string, makeError: ErrorMaker): Error | null {
  const value = claimsSummary[property];
  if (value === undefined) {
    return makeError(`"${property}" is undefined`);
  }
  if (typeof value !== 'number') {
    return makeError(`"${property}" with value: ${value} is ${typeof value} instead of a number`);
  }
  if (Number.isNaN(value)) {
    return makeError(`"${property}" is NaN`);
  }
  return null;
}
function validateString(claimsSummary: any, property: string, makeError: ErrorMaker): Error | null {
  const value = claimsSummary[property];
  if (value === undefined) {
    return makeError(`"${property}" is undefined`);
  }
  if (typeof value !== 'string') {
    return makeError(`"${property}" with value: ${value} is ${typeof value} instead of a string`);
  }
  return null;
}

/**
 * Parses the claims summary response from API into a typed ClaimsSummary object. If a number is sent as a string and can
 * be parsed, it will parse it and log a warning. Otherwise it will return an error if type expectations are not met.
 */
export function parseClaimsSummary(claimsSummary: RawClaimsSummary): ClaimsSummary | Error {
  function makeError(message: string): Error {
    const suffix = claimsSummary?.claims_summary_id
      ? ` for claims summary ID: ${claimsSummary.claims_summary_id}`
      : '';
    return new Error(`Invalid claims summary response: ${message}${suffix}`);
  }
  if (typeof claimsSummary !== 'object' || claimsSummary === null) {
    return makeError('not an object');
  }
  const coercedClaimsSummary = Object.entries(claimsSummary).reduce((acc: any, [key, value]) => {
    const numbersToCoerce = [...requiredNumbersOrNulls, ...requiredNumbers];
    // Coerce string numbers to actual numbers
    if (numbersToCoerce.includes(key) && typeof value === 'string' && !Number.isNaN(Number(value))) {
      acc[key] = Number(value);
      Logger.warn(`Unexpected string type for "${key}". Coercing to number.`);
    } else {
      acc[key] = value;
    }
    return acc;
  }, {});

  const maybeStringErrors = requiredStrings.map((property) =>
    validateString(coercedClaimsSummary, property, makeError),
  );
  const stringFailure = maybeStringErrors.find((result) => result instanceof Error);
  if (stringFailure) {
    return stringFailure as Error;
  }
  const maybeNumberErrors = requiredNumbers.map((property) =>
    validateNumber(coercedClaimsSummary, property, makeError),
  );
  const numberFailure = maybeNumberErrors.find((result) => result instanceof Error);
  if (numberFailure) {
    return numberFailure as Error;
  }
  const maybeNumberOrNullErrors = requiredNumbersOrNulls.map((property) =>
    coercedClaimsSummary[property] === null
      ? null
      : validateNumber(coercedClaimsSummary, property, makeError),
  );
  const numberOrNullFailure = maybeNumberOrNullErrors.find((result) => result instanceof Error);
  if (numberOrNullFailure) {
    return numberOrNullFailure as Error;
  }
  const {
    claims_summary_id,
    enrollment_event_id,
    payer_name,
    payer_code,
    deductible_cents,
    max_cents,
    oop_toward_deductible_cents,
    oop_toward_max_cents,
    reported_at,
    status,
  } = coercedClaimsSummary as RawClaimsSummary;

  const deductibleDollars = typeof deductible_cents === 'number' ? deductible_cents / 100 : null;
  const maxDollars = max_cents / 100;
  const oopTowardDeductibleDollars =
    typeof oop_toward_deductible_cents === 'number' ? oop_toward_deductible_cents / 100 : null;
  const oopTowardMaxDollars = oop_toward_max_cents / 100;

  return {
    claimsSummaryId: claims_summary_id,
    enrollmentEventId: enrollment_event_id,
    payerName: payer_name,
    payerCode: payer_code,
    deductibleDollars,
    maxDollars,
    oopTowardDeductibleDollars,
    oopTowardMaxDollars,
    reportedAt: reported_at,
    status,
  };
}
